// The sub-action view of the Initiatives tab (owner, 2026-10-20 on the
// iteration-2 review: "make sure you can click on sub-actions from everywhere
// they show up and there's a sub-action mode that shows all the details"):
// every live sub-action of the visible initiatives as its own row, carrying
// everything the sub-action pop-up knows. Pure rules; SubActionsTable.tsx draws them.

import type { InitiativeAction, InitiativeState } from '../../services/roadmapReports';
import { evidenceForAction } from './evidenceUtils';
import {
  actionBlockerRefs,
  actionDateSpan,
  actionHoldsUpRefs,
  actionIsLate,
  liveActions,
  type ActionRef,
} from './roadmapViewUtils';

export interface SubActionRow {
  initiative: InitiativeState;
  action: InitiativeAction;
  late: boolean;
  start: string | null;
  end: string | null;
  endKind: 'completed' | 'target' | null;
  /** Sibling sub-actions this one waits on. */
  waitsOn: ActionRef[];
  /** Sibling sub-actions that wait on this one. */
  holdsUp: ActionRef[];
  /** How many quotes Merlin attached to this sub-action. */
  quotes: number;
}

export interface SubActionGroup {
  initiative: InitiativeState;
  rows: SubActionRow[];
}

/**
 * One group per initiative that has live (not dropped) sub-actions, in the
 * order the initiatives came (the page's sort); inside a group the rows keep
 * Merlin's order, which follows the dependency chain. An initiative with no
 * sub-actions is not a row here: the initiative is the action, and the list
 * view has it.
 */
export function subActionGroups(initiatives: readonly InitiativeState[], today: string): SubActionGroup[] {
  const groups: SubActionGroup[] = [];
  for (const i of initiatives) {
    const live = liveActions(i.actions);
    if (live.length === 0) continue;
    const rows = live.map((a): SubActionRow => {
      const { start, end, endKind } = actionDateSpan(a);
      return {
        initiative: i,
        action: a,
        late: actionIsLate(a, today),
        start,
        end,
        endKind,
        waitsOn: actionBlockerRefs(a, live),
        holdsUp: actionHoldsUpRefs(a, live),
        quotes: evidenceForAction(i.evidence, a.key).length,
      };
    });
    groups.push({ initiative: i, rows });
  }
  return groups;
}

export interface SubActionTotals {
  rows: number;
  initiatives: number;
  open: number;
  inProgress: number;
  done: number;
  late: number;
  undated: number;
}

/** The counts the view's heading reads. "Undated" = not done and no stated end. */
export function subActionTotals(groups: readonly SubActionGroup[]): SubActionTotals {
  const t: SubActionTotals = { rows: 0, initiatives: groups.length, open: 0, inProgress: 0, done: 0, late: 0, undated: 0 };
  for (const g of groups) {
    for (const r of g.rows) {
      t.rows += 1;
      if (r.action.status === 'done') t.done += 1;
      else if (r.action.status === 'in_progress') t.inProgress += 1;
      else t.open += 1;
      if (r.late) t.late += 1;
      if (r.action.status !== 'done' && !r.end) t.undated += 1;
    }
  }
  return t;
}

/** The heading's one-line summary of the totals. */
export function subActionTotalsLine(t: SubActionTotals): string {
  const n = (k: number, word: string) => `${k} ${word}${k === 1 ? '' : 's'}`;
  const bits = [
    `${n(t.rows, 'sub-action')} on ${n(t.initiatives, 'initiative')}`,
    `${t.open} open`,
    `${t.inProgress} in progress`,
    `${t.done} done`,
  ];
  if (t.late > 0) bits.push(`${t.late} late`);
  if (t.undated > 0) bits.push(`${t.undated} with no date`);
  return bits.join(' · ');
}
